import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/_components/dialog";
import { Button } from "@/components/_components/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/_components/select";
import { Loader2 } from "lucide-react";
import * as api from "@/lib/api/client";
import { toast } from "react-hot-toast";

interface StartJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { appointmentTime: string; phoneNo: string; timezone: string; countryCode: string; finishTime?: string }) => Promise<void>;
  initialPhoneNo?: string;
  initialAppointmentDateTime?: string;
  initialCountryCode?: string;
}

interface Country {
  id: number;
  countryCode: string;
  countryName: string;
  isdCode: string;
  startWorkingHour: string;
  endWorkingHour: string;
}

export function StartJobDialog({ open, onOpenChange, onSubmit, initialPhoneNo, initialAppointmentDateTime, initialCountryCode }: StartJobDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);

  // Determine default timezone based on browser timezone
  const getDefaultTimezone = () => {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // If browser timezone is in America, default to PST, otherwise default to IST
    if (browserTz.startsWith('America/')) {
      return 'America/Los_Angeles';
    }
    return 'Asia/Kolkata';
  };

  // Convert ISO datetime to datetime-local format
  const formatDateTimeLocal = (dateTimeStr: string) => {
    if (!dateTimeStr || dateTimeStr === "N/A") return "";
    try {
      const date = new Date(dateTimeStr);
      // Format to YYYY-MM-DDTHH:mm for datetime-local input
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      return "";
    }
  };

  const [formData, setFormData] = useState({
    appointmentTime: "",
    phoneNo: "",
    timezone: getDefaultTimezone(),
    countryCode: "",
    finishTime: "",
  });

  // Update form data when dialog opens with initial values
  useEffect(() => {
    if (open) {
      const newFormData = {
        appointmentTime: initialAppointmentDateTime ? formatDateTimeLocal(initialAppointmentDateTime) : "",
        phoneNo: initialPhoneNo || "",
        timezone: getDefaultTimezone(),
        countryCode: initialCountryCode || "",
        finishTime: "",
      };
      setFormData(newFormData);
    }
  }, [open, initialPhoneNo, initialAppointmentDateTime, initialCountryCode]);

  // Fetch countries list when dialog opens
  useEffect(() => {
    if (open) {
      const fetchCountries = async () => {
        setIsLoadingCountries(true);
        try {
          const response = await api.getCountriesList();
          const countriesList = response.data || [];
          setCountries(countriesList);

          // Set country code after countries are loaded
          if (initialCountryCode) {
            const countryExists = countriesList.some((c: Country) => c.countryCode === initialCountryCode);
            if (countryExists) {
              setFormData(prev => ({ ...prev, countryCode: initialCountryCode }));
            }
          }
        } catch (error) {
          console.error("Error fetching countries:", error);
          toast.error("Failed to load countries list");
        } finally {
          setIsLoadingCountries(false);
        }
      };

      fetchCountries();
    }
  }, [open, initialCountryCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate phone number format: must start with + and contain only numbers after that
    const phoneRegex = /^\+\d+$/;
    if (!phoneRegex.test(formData.phoneNo)) {
      alert("Phone number must start with + and contain only numbers");
      return;
    }

    // Validate country code is selected
    if (!formData.countryCode) {
      alert("Please select a country code");
      return;
    }

    // Validate that finish time is greater than appointment time if provided
    if (formData.finishTime && formData.appointmentTime) {
      const startTime = new Date(formData.appointmentTime);
      const endTime = new Date(formData.finishTime);

      if (endTime <= startTime) {
        alert("Appointment End Date Time must be greater than Appointment Start Date Time");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Only include finishTime if it's provided
      const submitData = {
        ...formData,
        ...(formData.finishTime && { finishTime: formData.finishTime }),
      };
      await onSubmit(submitData);
      onOpenChange(false);
    } catch (error) {
      console.error("Error starting job:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Start Job</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="appointmentTime">Appointment Start Date Time</Label>
              <Input
                id="appointmentTime"
                type="datetime-local"
                value={formData.appointmentTime}
                onChange={(e) => setFormData(prev => ({ ...prev, appointmentTime: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="finishTime">Appointment End Date Time (Optional)</Label>
              <Input
                id="finishTime"
                type="datetime-local"
                value={formData.finishTime}
                onChange={(e) => setFormData(prev => ({ ...prev, finishTime: e.target.value }))}
                min={formData.appointmentTime || undefined}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phoneNo">Worker Phone No</Label>
              <Input
                id="phoneNo"
                type="tel"
                value={formData.phoneNo}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, phoneNo: e.target.value }));
                }}
                placeholder="+1234567890"
                required
              />
              <p className="text-xs text-muted-foreground">
                Phone number must start with + and contain only numbers
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="timeZone">Time Zone</Label>
              <Select
                value={formData.timezone}
                onValueChange={(value) => setFormData(prev => ({ ...prev, timezone: value }))}
              >
                <SelectTrigger id="timeZone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</SelectItem>
                  <SelectItem value="Asia/Kolkata">Asia/Kolkata (IST)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="countryCode">Country Code</Label>
              <Select
                value={formData.countryCode}
                onValueChange={(value) => setFormData(prev => ({ ...prev, countryCode: value }))}
                disabled={isLoadingCountries}
              >
                <SelectTrigger id="countryCode">
                  <SelectValue placeholder={isLoadingCountries ? "Loading countries..." : "Select a country"} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] overflow-y-auto">
                  {countries.map((country) => (
                    <SelectItem key={country.id} value={country.countryCode}>
                      {country.countryName} ({country.countryCode})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                "Save and Start"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
